"use client";
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState, useCallback } from "react";
import dynamic from 'next/dynamic'
import type { TableProps as RCTableProps } from 'rc-table';
import { Loading } from "../loading";
import { InfiniteScrollDetector } from "../infiniteScroll";
import { VirtualRCTableViewProps } from "./types";

const TableComponent = dynamic(() => import('rc-table'), {
    ssr: false,
})

// 类型安全的 Table 组件包装
const Table = TableComponent as <T extends Record<string, any>>(props: RCTableProps<T>) => React.JSX.Element

// 使用 ES6 类封装可视行状态管理
class VisibleRowsManager {
    private indices: Set<number>;
    private _minIndex: number;
    private _maxIndex: number;

    constructor() {
        this.indices = new Set<number>();
        this._minIndex = -1;
        this._maxIndex = -1;
    }

    get minIndex(): number {
        return this._minIndex;
    }

    get maxIndex(): number {
        return this._maxIndex;
    }

    updateIndices(index: number, isVisible: boolean): void {
        if (isVisible) {
            this.indices.add(index);
        } else {
            this.indices.delete(index);
        }

        this.recalculateRange();
    }

    private recalculateRange(): void {
        if (this.indices.size === 0) {
            this._minIndex = -1;
            this._maxIndex = -1;
            return;
        }

        const validIndices = Array.from(this.indices).filter(idx => idx >= 0);
        if (validIndices.length > 0) {
            this._minIndex = Math.min(...validIndices);
            this._maxIndex = Math.max(...validIndices);
        } else {
            this._minIndex = -1;
            this._maxIndex = -1;
        }
    }

    getVisibleIndices(): number[] {
        return Array.from(this.indices)
            .filter(idx => idx >= 0)
            .sort((a, b) => a - b);
    }

    getVisibleRange(): { min: number; max: number } {
        return { min: this._minIndex, max: this._maxIndex };
    }

    clear(): void {
        this.indices.clear();
        this._minIndex = -1;
        this._maxIndex = -1;
    }

    shouldBeVisible(index: number, preloadRowCount: number): boolean {
        // 如果没有可见行，显示所有行（智能初始状态）
        if (this._minIndex === -1 || this._maxIndex === -1) {
            return true;
        }

        // 如果索引无效，也显示
        if (index < 0) {
            return true;
        }

        const preloadStart = Math.max(0, this._minIndex - preloadRowCount);
        const preloadEnd = this._maxIndex + preloadRowCount;

        return index >= preloadStart && index <= preloadEnd;
    }
}

const CustomRow = (props: PropsWithChildren<any>) => {
    const { children, rowHeight = 60, preloadRowCount, dataIndex, visibleRowsManager, ...restProps } = props;

    const rowRef = useRef<HTMLTableRowElement>(null);
    // 使用更简单的初始状态管理：所有行初始为false，让IntersectionObserver控制
    const [isVisible, setIsVisible] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // 创建 observer 的函数，便于窗口调整时重新创建
    const createObserver = useCallback(() => {
        // 检查环境是否支持 IntersectionObserver
        if (typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            if (visibleRowsManager && typeof dataIndex === 'number') {
                visibleRowsManager.updateIndices(dataIndex, true);
            }
            return null;
        }

        try {
            // 计算需要预加载的行高
            const preloadMargin = `${rowHeight * preloadRowCount}px`;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const newIsVisible = entry.isIntersecting;
                        setIsVisible(newIsVisible);

                        if (visibleRowsManager && typeof dataIndex === 'number') {
                            visibleRowsManager.updateIndices(dataIndex, newIsVisible);
                        }
                    });
                },
                {
                    threshold: 0.1, // 当至少 10% 的行可见时，认为它在可视区域内
                    // 设置 rootMargin 使得在实际可视区域上下各扩展预加载区域
                    rootMargin: `${preloadMargin} 0px ${preloadMargin} 0px`,
                },
            );

            return observer;
        } catch (error) {
            // 出错时默认显示所有行
            setIsVisible(true);
            if (visibleRowsManager && typeof dataIndex === 'number') {
                visibleRowsManager.updateIndices(dataIndex, true);
            }
            return null;
        }
    }, [rowHeight, preloadRowCount, dataIndex, visibleRowsManager]);

    // 初始化和清理 observer
    useEffect(() => {
        // 服务器端渲染时不执行
        if (typeof window === 'undefined') { return; }

        const currentRef = rowRef.current;
        if (!currentRef) { return; }

        // 创建 observer
        observerRef.current = createObserver();

        // 如果成功创建了 observer，开始观察
        if (observerRef.current) {
            observerRef.current.observe(currentRef);
        }

        return () => {
            // 清理 observer
            if (observerRef.current && currentRef) {
                observerRef.current.unobserve(currentRef);
                observerRef.current.disconnect();
            }

            // 从全局状态中移除此行
            if (visibleRowsManager && typeof dataIndex === 'number') {
                visibleRowsManager.updateIndices(dataIndex, false);
            }
        };
    }, [createObserver, dataIndex, visibleRowsManager]);

    // 处理窗口大小调整
    useEffect(() => {
        // 服务器端渲染时不执行
        if (typeof window === 'undefined') { return; }

        const handleResize = () => {
            const currentRef = rowRef.current;
            if (!currentRef) { return; }

            // 清理旧的 observer
            if (observerRef.current) {
                observerRef.current.unobserve(currentRef);
                observerRef.current.disconnect();
            }

            // 创建新的 observer
            observerRef.current = createObserver();

            // 如果成功创建了 observer，开始观察
            if (observerRef.current) {
                observerRef.current.observe(currentRef);
            }
        };

        // 添加窗口大小调整事件监听
        window.addEventListener('resize', handleResize);

        return () => {
            // 移除事件监听
            window.removeEventListener('resize', handleResize);
        };
    }, [createObserver]);

    // 使用 useMemo 缓存可见性检查
    const shouldRender = useMemo(() => {
        if (!visibleRowsManager || typeof dataIndex !== 'number') {
            return true;
        }

        // 对于前几行，总是渲染（确保初始内容显示）
        if (dataIndex < Math.min(preloadRowCount, 10)) {
            return true;
        }

        // 如果行当前可见，总是渲染
        if (isVisible) {
            return true;
        }

        // 否则使用预加载逻辑判断
        return visibleRowsManager.shouldBeVisible(dataIndex, preloadRowCount);
    }, [visibleRowsManager, dataIndex, preloadRowCount, isVisible]);

    return (
        <tr
            {...restProps}
            ref={rowRef}
            data-visible={isVisible}
            style={{ ...restProps.style, height: rowHeight }}
            data-row-index={isVisible ? dataIndex : null}
        >
            {shouldRender ? children : null}
        </tr>
    );
};



export const VirtualRCTableView = <T extends Record<string, any>>(props: VirtualRCTableViewProps<T>) => {
    const {
        rowHeight,
        preloadRowCount = 15, // 增加默认预加载行数，确保有足够数据显示
        onRow,
        enableInfiniteScroll = false,
        onLoadMore,
        loadingMore = false,
        hasMore = true,
        loadMoreThreshold = 100,
        loadingMoreContent,
        noMoreContent,
        ...restProps
    } = props;

    // 使用 useMemo 缓存 visibleRowsManager 实例，避免不必要的重新渲染
    const visibleRowsManager = useMemo(() => {
        return new VisibleRowsManager()
    }, []);

    // 定义自定义组件
    const components: VirtualRCTableViewProps<T>['components'] = useMemo(() => {
        return {
            body: {
                row: (rowProps) => {
                    const { 'data-row-index': dataIndex, ...restRowProps } = rowProps;
                    return <CustomRow
                        key={`table-row-${dataIndex}`}
                        {...restRowProps}
                        dataIndex={dataIndex}
                        rowHeight={rowHeight}
                        visibleRowsManager={visibleRowsManager}
                        preloadRowCount={preloadRowCount}
                    />;
                },
            },
        };
    }, [rowHeight, preloadRowCount, visibleRowsManager]);

    // // 将方法暴露到全局，供外部调用（仅在开发环境）
    // useEffect(() => {
    //     if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    //         // 避免重复绑定
    //         (window as any).getTableVisibleRowIndices = _getVisibleRowIndices;
    //         (window as any).getTableVisibleRowRange = _getVisibleRowRange;
    //     }

    //     return () => {
    //         if (typeof window !== 'undefined') {
    //             try {
    //                 delete (window as any).getTableVisibleRowIndices;
    //                 delete (window as any).getTableVisibleRowRange;
    //             } catch (error) {
    //                 console.error('Error cleaning up table methods:', error);
    //             }
    //         }
    //     };
    // }, [_getVisibleRowIndices, _getVisibleRowRange]);

    // 默认的加载和无更多数据的内容
    const defaultLoadingContent = (
        <Loading fill="#fff" />
    );

    // const defaultNoMoreContent = (
    //     <div style={{ color: '#999', fontSize: '14px' }}>
    //         没有更多数据了
    //     </div>
    // );

    return (
        <>
            <Table<T>
                {...restProps}
                components={components}
                onRow={(record, index) => {
                    // 返回正确的类型，确保与 HTMLAttributes 兼容
                    return {
                        'data-row-index': index,
                        style: {}, // 添加一个空的 style 对象以确保类型兼容
                        ...onRow?.(record, index),
                    };
                }}
                emptyText={""}
                footer={enableInfiniteScroll ? () => (
                    <InfiniteScrollDetector
                        onLoadMore={onLoadMore}
                        loadingMore={loadingMore}
                        hasMore={hasMore}
                        threshold={loadMoreThreshold}
                        loadingContent={loadingMoreContent || defaultLoadingContent}
                        noMoreContent={noMoreContent}
                    />
                ) : undefined}
            />

        </>
    );
}